import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { resolveAccess } from '@/lib/auth/access';
import { useSession } from '@/lib/auth/session';

/**
 * 로그인·메뉴 접근 여부에 따라 화면을 돌려보낸다.
 *
 * **이것은 보안 장치가 아니다.** 실제 인가는 백엔드가 매 요청마다 판정한다 — 이 컴포넌트를 통째로
 * 지워도 데이터가 새어 나가서는 안 된다. "무엇을 보여줄지"의 판단은 `lib/auth/access.ts` 의 순수
 * 함수(`resolveAccess`)가 하고, 여기서는 그 결정을 **웹 라우팅/화면**으로 옮기기만 한다. 판단과
 * 화면 기술을 나눈 이유는 추후 네이티브 앱이 같은 결정을 자기 내비게이션으로 옮겨 쓰기 위해서다.
 * (배경: docs/04_2026-07-28_cross-platform-mobile-design.md)
 */
export function RequireAuth({ menuKey, children }: { menuKey?: string; children: ReactNode }) {
  const location = useLocation();
  const { data: session, isPending, isError, isFetching, refetch } = useSession();

  const decision = resolveAccess({ session, isPending, isError, menuKey });

  switch (decision.kind) {
    case 'pending':
      return (
        <div className="flex justify-center py-24">
          <div className="spinner w-8 h-8" />
        </div>
      );

    // 서버 불통은 로그아웃이 아니므로 로그인으로 보내지 않는다 — 보내면 서버가 살아날 때까지
    // 로그인↔보호경로를 오가며 깜빡인다. 대신 자동 재시도 화면을 세운다.
    case 'unreachable':
      return <SessionUnavailable onRetry={refetch} retrying={isFetching} />;

    case 'unauthenticated': {
      const returnTo = encodeURIComponent(location.pathname + location.search);
      return <Navigate to={`/login?returnTo=${returnTo}`} replace />;
    }

    /*
      이미 로그인돼 있으므로 로그인 화면으로 보내면 곧바로 되돌아와 무한 왕복이 된다. 홈으로
      되돌리면서 사유를 주소에 싣고, 홈이 그것을 한 번 보여 준다.

      <b>로그인이 보낸 길이면 사유를 싣지 않는다</b> (사용자 지시 2026-09-14). 로그인은 끊기기
      전에 보던 주소로 사람을 되돌리는데({@code returnTo}), 그 화면이 권한 밖이면 <b>누른 적도
      없는 이동</b>이 「권한이 없습니다」 로 끝난다. 막는 것은 그대로 막되, 말은 하지 않는다.
    */
    case 'denied': {
      const fromLogin = (location.state as { fromLogin?: boolean } | null)?.fromLogin === true;
      return fromLogin
        ? <Navigate to="/home" replace />
        : <Navigate to={`/home?denied=${encodeURIComponent(decision.menuKey)}`} replace />;
    }

    case 'granted':
      return <>{children}</>;
  }
}

/**
 * 서버에 닿지 못해 세션을 확인할 수 없을 때 세우는 화면.
 *
 * 로그아웃이 아니라 "연결 문제"이므로 로그인 폼 대신 재시도를 안내한다. 창을 다시 포커스하면
 * `refetchOnWindowFocus` 로 이미 재조회가 걸리고, 그와 별개로 여기서도 주기적으로 자동 재시도해
 * 서버가 살아나면 사용자가 아무것도 누르지 않아도 원래 화면으로 돌아간다.
 */
function SessionUnavailable({ onRetry, retrying }: { onRetry: () => void; retrying: boolean }) {
  useEffect(() => {
    const timer = window.setInterval(() => onRetry(), 5_000);
    return () => window.clearInterval(timer);
  }, [onRetry]);

  return (
    <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
      <div className="spinner w-8 h-8" />
      <div>
        <p className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
          서버에 연결할 수 없습니다
        </p>
        <p className="mt-1 text-sm" style={{ color: 'var(--text-muted)' }}>
          네트워크나 서버 상태를 확인하는 중입니다. 잠시 후 자동으로 다시 시도합니다.
        </p>
      </div>
      <button type="button" onClick={() => onRetry()} className="btn-secondary" disabled={retrying}>
        {retrying ? '다시 시도 중…' : '다시 시도'}
      </button>
    </div>
  );
}
